import type { Node, Schema } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { setStoredTextStyleMark } from './textStyleHelpers';

interface UpdateListMarkerColorsOptions {
  isNewList?: boolean;
  emptyItemColor?: string | null;
  selectionColor?: string | null;
}

/**
 * Updates the bullet/number marker color for list items in the given range.
 *
 * @param tr - The ProseMirror transaction to apply changes to.
 * @param from - Start of the range to update.
 * @param to - End of the range to update.
 * @param isNewList - When true (list just toggled on), derives marker color from existing text color.
 * @param emptyItemColor - Fallback color for empty items when toggling a new list on.
 * @param selectionColor - The new color being applied. Only applies to existing list items that are fully covered by the selection.
 * @returns true if at least one list item was found and processed.
 */

export function updateListMarkerColors(
  tr: Transaction,
  from: number,
  to: number,
  { isNewList, emptyItemColor, selectionColor }: UpdateListMarkerColorsOptions = {}
): boolean {
  let processed = false;
  tr.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name !== 'listItem') return;
    processed = true;

    if (isNewList) {
      if (!node.textContent) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, color: emptyItemColor ?? null });
      } else {
        const color = getListItemColor(node);
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, color });
      }
      return;
    }

    if (!node.textContent) {
      // If the list item is empty, update the marker color to match the new text color
      const color = selectionColor ?? null;
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, color });
    } else {
      // only update the marker color to match the new text color if the entire list item is covered by the selection.
      const textStart = pos + 2; // skip the <li> and <p> open tokens.
      const textEnd = pos + node.nodeSize - 2; // skip the <li> and <p> close tokens.
      const itemFullyCovered = from <= textStart && to >= textEnd;
      if (itemFullyCovered) tr.setNodeMarkup(pos, undefined, { ...node.attrs, color: selectionColor });
    }
  });
  return processed;
}

// Returns the text color of a list item if all its text shares the same color, null otherwise.
export function getListItemColor(listItemNode: Node): string | null {
  const colors: (string | null)[] = [];

  listItemNode.descendants((node) => {
    if (node.isText) {
      const textStyleMark = node.marks.find((m) => m.type.name === 'textStyle');
      colors.push(textStyleMark?.attrs.color ?? null);
    }
  });

  const firstColor = colors[0];
  return colors.every((c) => c === firstColor) ? firstColor : null;
}

export function applyListToggleColors(
  tr: Transaction,
  schema: Schema,
  from: number,
  to: number,
  activeTextStyleAttrs: Record<string, unknown>,
  isNewList: boolean
): void {
  const activeColor: string | null = typeof activeTextStyleAttrs.color === 'string' ? activeTextStyleAttrs.color : null;
  if (isNewList) {
    updateListMarkerColors(tr, tr.mapping.map(from), tr.mapping.map(to), { isNewList: true, emptyItemColor: activeColor });
  }
  if (activeColor) {
    setStoredTextStyleMark(tr, schema, activeTextStyleAttrs);
  }
}
